<template lang="pug">
  y-dialog(v-model="showDialog" :fullscreen="$vuetify.breakpoint.xsOnly" :max-width="maxWidth" persistent scrollable content-class="round-3xl")
    v-card
      v-card-title.pa-4
        v-row(no-gutters align="center")
          v-col(cols="10" offset="1")
            div.text-xl.font-bold.neutral700--text.text-center {{ $t('create_input_field') }}
          v-col(cols="1" align="right")
            v-btn.ml-n3(text icon @click="closeDialog")
              v-icon(color="neutral700" :size="24") mdi-close

      v-divider
      v-card-text(:class="$vuetify.breakpoint.xsOnly ? 'pa-4' : 'pa-8'")
        v-form(ref="form")
          v-row(no-gutters)
            v-col
              div.text-md.font-semibold.neutral700--text {{ $t('fill_the_information_below') }}
              .form-input
                div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('input_type') }}
                v-select.text-sm.mt-2.round-xl(v-model="form.datatype" :items="types" :placeholder="$t('select_input_type')" item-text="text" item-value="value" outlined :rules="rules.not_empty_select" hide-details="auto" dense :disabled="Boolean(form.id)" :class="Boolean(form.id) ? 'basic400' : ''")
                div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('title') }}
                v-text-field.text-sm.mt-2.round-xl(v-model="form.name" :placeholder="$t('enter_title')" outlined :rules="rules.not_empty" hide-details="auto" dense)
                div(v-if="hasPlaceholder")
                  div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('placeholder') }} ({{ $t('optional') }})
                  v-text-field.text-sm.mt-2.round-xl(v-model="form.placeholder" outlined :placeholder="$t('enter_placeholder')" :hint="$t('placeholder_desc')" persistent-hint dense)
                .mt-6.text-md.font-semibold.neutral700--text {{ $t('settings') }}
                v-row.mt-2(no-gutters)
                  v-col(:cols="form.datatype === 'selection' ? 6 : 5")
                    div.mb-3.text-sm.neutral700--text {{ $t('make_required') }}
                    template(v-if="form.datatype === 'selection'")
                      div.mb-3.text-sm.neutral700--text {{ $t('allow_multiple_selection') }}
                      div.mb-3.text-sm.neutral700--text Add "Other" option
                  v-col
                    y-checkbox.mb-1(v-model="form.required")
                    template(v-if="form.datatype === 'selection'")
                      y-checkbox.mb-1(v-model="form.allow_multiple_selection")
                      y-checkbox.mb-1(v-model="form.enable_other")
            template(v-if="form.datatype === 'selection'")
              v-col.mx-6.shrink
                v-divider(vertical)
              v-col
                .list-answer
                  div.text-sm.font-semibold.neutral500--text.mt-4 List of options
                  v-row.mt-2(v-for="(choice, cindex) in choices" no-gutters :key="'choice-' + cindex")
                    v-col
                      v-text-field.text-sm.round-xl(v-model="choice.text" :placeholder="$t('enter_answer')" outlined :rules="rules.not_empty" hide-details="auto" dense)
                    v-col.shrink(v-if="cindex !== 0")
                      v-btn.ml-3(icon small @click="removeItem(choice)" style="margin-top: 6px")
                        v-icon(color="neutral700") $yzi-trash-03
                  v-row.mt-2(v-if="form.enable_other" no-gutters)
                    v-col
                      v-text-field.text-sm.round-xl(value="Others" background-color="neutral300" outlined hide-details="auto" disabled dense)
                  v-btn.button-xs.mt-2(color="neutral700" outlined @click="addItem")
                    v-icon(color="neutral700" left) $yzi-plus
                    | {{ $t('add_answer') }}

      v-divider
      v-card-actions.py-4(:class="$vuetify.breakpoint.xsOnly ? 'px-4' : 'px-8'")
        v-btn.button-md.neutral700--text.px-0.underline(@click="closeDialog" text) {{ $t('cancel') }}
        v-spacer
        v-btn.button-md(:loading="isLoading" color="neutral700" depressed dark @click="saveData") {{ $t('save') }}

</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  props: {
    open: {
      type: Boolean,
    },
    field: {
      type: Object,
      default: (() => {})
    },
    allFields: {
      type: Array,
      default: () => {
        return []
      }
    }
  },
  data () {
    return {
      isLoading: false,
      form: {},
      choices: [{ text: null }],
      rules: {
        not_empty: [val => (val || '').length > 0 || 'This field is required'],
        has_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required'],
        has_price: [(v) => !!v || 'This field is required', (v) => v >= 1|| `Amount should be more than ${this.$currency.symbol} 1`],
        not_empty_select: [v => !!v || 'This field is required']
      },
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
    }),
    showDialog: {
      get () {
        return this.open
      },
      set (val) {
        this.$emit('update:open', val)
      }
    },
    types() {
      return [
        { text: 'Short answer', value: 'string' },
        { text: 'Multiple choice', value: 'selection' },
        { text: 'Text area field', value: 'text' },
        { text: 'Number only', value: 'number' },
        { text: 'Date', value: 'date' },
        { text: 'Phone number', value: 'phone' },
        { text: 'Email', value: 'email', disabled: this.isFieldDisabled('email') },
        { text: 'Address', value: 'address', disabled: this.isFieldDisabled('address') },
      ]
    },
    hasPlaceholder() {
      const hasPlaceholders = ['string', 'text', 'number', 'email', 'date']
      return hasPlaceholders.includes(this.form.datatype)
    },
    maxWidth() {
      if (this.form.datatype === 'selection') {
        return 900
      }
      return 600
    },
    emailDisabled() {
      let hasEmailField = false
      if (this.field) {
        hasEmailField = this.allFields.filter(field => field.datatype === 'email' && this.field.id !== field.id).length > 0
      } else {
        hasEmailField = this.allFields.filter(field => field.datatype === 'email').length > 0
      }
      return hasEmailField
    },
  },
  mounted () {
    this.setData()
  },
  methods: {
    ...mapActions(['showMessage']),
    setData () {
      if (!this.field || !this.field.id) {
        return
      }
      let form = { ...this.field }
      form = {
        ...form,
        name: form.extra_options?.display_name || form.field_name || null,
        placeholder: form.extra_options.placeholder || null,
        required: form.extra_options.required || false,
      }
      if (form.datatype === 'selection') {
        form.allow_multiple_selection = form.extra_options.type === 'checklist'
        form.enable_other = form.extra_options.enable_other || false
        this.choices = form.extra_options.choices.map(m => { return { text: m } })
      }
      this.form = form
    },
    async saveData () {
      if (!this.$refs.form.validate()) {
        return
      }
      this.isLoading = true
      let form = {
        field_name: this.form.name,
        datatype: this.form.datatype,
        status: 'active',
        extra_options: {
          display_name: this.form.name,
          required: this.form.required,
          placeholder: this.form.placeholder,
        }
      }
      if (this.form.datatype === 'selection') {
        form.extra_options.choices = this.choices.map(m => m.text)
        form.extra_options.type = this.form.allow_multiple_selection ? 'checklist' : 'radiobtn'
        form.extra_options.enable_other = this.form.enable_other || false
      }
      try {
        const { data } = this.field && this.field.id ? await this.$axios.patch(`/clinics/${this.company.slug}/patient_custom_fields/${this.field.id}`, form) : await this.$axios.post(`/clinics/${this.company.slug}/patient_custom_fields`, form)
        this.$emit('updated', data)
        this.showMessage(['success', this.$t('success_message.update_successful')])
        this.closeDialog()
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoading = false
      }
    },
    addItem () {
      this.choices.push({ text: null })
    },
    removeItem (choice) {
      this.choices = this.choices.filter(f => f !== choice)
    },
    closeDialog () {
      this.showDialog = false
      this.$emit('closed')
    },
    isFieldDisabled(fieldType) {
      let hasExistingField = false
      if (this.field) {
        hasExistingField = this.allFields.filter((field) => field.datatype === fieldType && this.field.id !== field.id).length > 0
      } else {
        hasExistingField = this.allFields.filter(field=> field.datatype === fieldType).length > 0
      }
      return hasExistingField
    }
  }
}
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-basic500);
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
</style>